import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { GuidedOrderStatus, GuidedPaymentStatus } from '@/types/guidedOrder';

const STATUS_CONFIG: Record<
  GuidedOrderStatus,
  { label: string; className: string; tooltip: string }
> = {
  awaiting_intake: {
    label: 'Awaiting Enrollment',
    className: 'bg-slate-100 text-slate-700 border-slate-300',
    tooltip: 'The patient has not yet completed the enrollment form or clinical intake.',
  },
  intake_completed: {
    label: 'Enrollment Done',
    className: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    tooltip: 'The patient has completed enrollment and is awaiting clinical review.',
  },
  mdi_submitted: {
    label: 'Under Review',
    className: 'bg-purple-100 text-purple-800 border-purple-300',
    tooltip: 'A clinician is currently reviewing this order.',
  },
  mdi_completed: {
    label: 'Under Review',
    className: 'bg-purple-100 text-purple-800 border-purple-300',
    tooltip: 'A clinician is currently reviewing this order.',
  },
  rx_verified: {
    label: 'Rx Verified',
    className: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    tooltip: 'The provider has reviewed and approved the prescription for this patient.',
  },
  fulfilled: {
    label: 'Fulfilled',
    className: 'bg-green-100 text-green-800 border-green-300',
    tooltip: 'This order has been fulfilled and shipped or dispensed.',
  },
  cancelled: {
    label: 'Cancelled',
    className: 'bg-red-100 text-red-800 border-red-300',
    tooltip: 'This order has been cancelled.',
  },
  expired: {
    label: 'Expired',
    className: 'bg-amber-100 text-amber-800 border-amber-300',
    tooltip: 'This order has expired and is no longer active.',
  },
};

const PAYMENT_STATUS_CONFIG: Record<
  GuidedPaymentStatus,
  { label: string; className: string; tooltip: string }
> = {
  pending: {
    label: 'Pending',
    className: 'bg-slate-100 text-slate-600 border-slate-300',
    tooltip: 'Payment has not yet been initiated.',
  },
  deferred: {
    label: 'Deferred',
    className: 'bg-amber-100 text-amber-700 border-amber-300',
    tooltip: 'Payment processing has been delayed or scheduled for a later date.',
  },
  hold_placed: {
    label: 'Hold Placed',
    className: 'bg-blue-100 text-blue-800 border-blue-300',
    tooltip: 'A temporary authorization hold has been placed on the patient\'s card.',
  },
  captured: {
    label: 'Captured',
    className: 'bg-green-100 text-green-800 border-green-300',
    tooltip: 'Funds have been successfully withdrawn from the patient\'s account.',
  },
  capture_failed: {
    label: 'Capture Failed',
    className: 'bg-red-100 text-red-800 border-red-300',
    tooltip: 'The payment capture attempt was unsuccessful.',
  },
  cancelled: {
    label: 'Released',
    className: 'bg-slate-100 text-slate-500 border-slate-300',
    tooltip: 'The payment hold has been released due to cancellation.',
  },
  refunded: {
    label: 'Refunded',
    className: 'bg-orange-100 text-orange-700 border-orange-300',
    tooltip: 'Funds have been returned to the patient\'s account.',
  },
  pending_amendment: {
    label: 'Pending Amendment',
    className: 'bg-orange-100 text-orange-800 border-orange-300',
    tooltip: 'An adjustment to the order price/details is awaiting patient approval.',
  },
};

export interface GuidedOrderStatusBadgeProps {
  status: GuidedOrderStatus;
  hasPendingCancellation?: boolean;
  className?: string;
}

export function GuidedOrderStatusBadge({ status, hasPendingCancellation, className }: GuidedOrderStatusBadgeProps) {
  if (hasPendingCancellation) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help">
              <Badge
                variant="outline"
                className={cn('bg-red-100 text-red-700 border-red-300 border', className)}
              >
                Cancellation Requested
              </Badge>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">A cancellation request is pending super admin approval.</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const config = STATUS_CONFIG[status] ?? {
    label: status,
    className: 'bg-slate-100 text-slate-700 border-slate-300',
    tooltip: '',
  };
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-help">
            <Badge
              variant="outline"
              className={cn(config.className, 'border', className)}
            >
              {config.label}
            </Badge>
          </span>
        </TooltipTrigger>
        {config.tooltip && (
          <TooltipContent side="top">{config.tooltip}</TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

export interface GuidedPaymentStatusBadgeProps {
  paymentStatus: GuidedPaymentStatus;
  className?: string;
}

export function GuidedPaymentStatusBadge({ paymentStatus, className }: GuidedPaymentStatusBadgeProps) {
  const config = PAYMENT_STATUS_CONFIG[paymentStatus] ?? {
    label: paymentStatus,
    className: 'bg-slate-100 text-slate-700 border-slate-300',
    tooltip: '',
  };
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-help">
            <Badge
              variant="outline"
              className={cn(config.className, 'border', className)}
            >
              {config.label}
            </Badge>
          </span>
        </TooltipTrigger>
        {config.tooltip && (
          <TooltipContent side="top">{config.tooltip}</TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}
